
import { useState } from 'react';

interface OptimizedImageProps {
  src: string;
  alt: string;
  className?: string;
  loading?: 'lazy' | 'eager';
  width?: number;
  height?: number;
  sizes?: string;
}

const OptimizedImage = ({
  src,
  alt,
  className = '',
  loading = 'lazy',
  width,
  height,
  sizes = '(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw'
}: OptimizedImageProps) => {
  const [imgError, setImgError] = useState(false);

  // Extract dimensions from Readdy API URL
  const extractDimensions = (url: string): { width: number; height: number } | null => {
    const widthMatch = url.match(/width=(\d+)/);
    const heightMatch = url.match(/height=(\d+)/);
    if (widthMatch && heightMatch) {
      return {
        width: parseInt(widthMatch[1], 10),
        height: parseInt(heightMatch[1], 10)
      };
    }
    return null;
  };

  // Generate URL with specific dimensions
  const generateSizedUrl = (url: string, targetWidth: number, format?: string): string => {
    if (!url.includes('readdy.ai/api/search-image')) {
      return url;
    }

    const dimensions = extractDimensions(url);
    if (!dimensions) return url;

    // Calculate proportional height
    const aspectRatio = dimensions.height / dimensions.width;
    const targetHeight = Math.round(targetWidth * aspectRatio);

    // Replace width and height in URL
    let newUrl = url
      .replace(/width=\d+/, `width=${targetWidth}`)
      .replace(/height=\d+/, `height=${targetHeight}`);

    // Add format if specified
    if (format) {
      const separator = newUrl.includes('?') ? '&' : '?';
      newUrl = `${newUrl}${separator}format=${format}`;
    }

    return newUrl;
  };

  // Generate srcset for different screen sizes
  const generateSrcSet = (url: string, format?: string): string => {
    if (!url.includes('readdy.ai/api/search-image')) {
      return url;
    }

    const dimensions = extractDimensions(url);
    if (!dimensions) return url;

    // Define breakpoints: mobile (480), tablet (768), desktop (1024), large (1920)
    const breakpoints = [480, 768, 1024, 1440, 1920];
    
    // Filter breakpoints that make sense for this image
    const validBreakpoints = breakpoints.filter(bp => bp <= dimensions.width * 1.5);
    
    // Always include at least the original size
    if (validBreakpoints.length === 0 || !validBreakpoints.includes(dimensions.width)) {
      validBreakpoints.push(dimensions.width);
    }

    const srcSetEntries = validBreakpoints.map(bp => {
      const sizedUrl = generateSizedUrl(url, bp, format);
      return `${sizedUrl} ${bp}w`;
    });

    return srcSetEntries.join(', ');
  };

  // Generate WebP URL
  const getWebPUrl = (url: string): string => {
    if (url.includes('readdy.ai/api/search-image')) {
      const separator = url.includes('?') ? '&' : '?';
      return `${url}${separator}format=webp`;
    }
    if (url.match(/\.(jpg|jpeg|png)$/i)) {
      return url.replace(/\.(jpg|jpeg|png)$/i, '.webp');
    }
    return url;
  };

  const isReaddyImage = src.includes('readdy.ai/api/search-image');
  const webpSrcSet = isReaddyImage ? generateSrcSet(src, 'webp') : undefined;
  const defaultSrcSet = isReaddyImage ? generateSrcSet(src) : undefined;
  const webpSrc = getWebPUrl(src);

  if (imgError) {
    // Fallback to original image without srcset if there's an error
    return (
      <img
        src={src}
        alt={alt}
        className={className}
        loading={loading}
        width={width}
        height={height}
      />
    );
  }

  return (
    <picture>
      {/* WebP sources with srcset for responsive images */}
      {webpSrcSet && (
        <source
          srcSet={webpSrcSet}
          sizes={sizes}
          type="image/webp"
        />
      )}
      {/* Fallback sources with srcset */}
      {defaultSrcSet && (
        <source
          srcSet={defaultSrcSet}
          sizes={sizes}
        />
      )}
      {/* Default WebP source without srcset as fallback */}
      <source srcSet={webpSrc} type="image/webp" />
      {/* Final fallback img element */}
      <img
        src={src}
        alt={alt}
        className={className}
        loading={loading}
        width={width}
        height={height}
        srcSet={defaultSrcSet}
        sizes={defaultSrcSet ? sizes : undefined}
        onError={() => setImgError(true)}
      />
    </picture>
  );
};

export default OptimizedImage;
